export interface WorksCategory {
  id: number
  name: string
  slug: string
}

export interface WorksTag {
  id: number
  name: string
  slug: string
}

export interface WorksImage {
  ID?: number
  id?: number
  url?: string
  alt?: string
  title?: string
  caption?: string
  sizes?: {
    thumbnail?: string
    medium?: string
    large?: string
    full?: string
    [key: string]: string | undefined
  }
  width?: number
  height?: number
}

export interface WorksVideo {
  ID?: number
  id?: number
  title?: string
  filename?: string
  filesize?: number
  url?: string
  link?: string
  alt?: string
  author?: string
  description?: string
  caption?: string
  name?: string
  status?: string
  uploaded_to?: number
  date?: string
  modified?: string
  menu_order?: number
  mime_type?: string
  type?: string
  subtype?: string
  icon?: string
  width?: number
  height?: number
}

export interface WorksTextContent {
  acf_fc_layout: 'text'
  chars: string
}

export interface WorksImageContent {
  acf_fc_layout: 'image'
  source: WorksImage
  caption: string
}

export interface WorksVideoContent {
  acf_fc_layout: 'video'
  source: WorksVideo
  caption: string
}

export type WorksContent = WorksTextContent | WorksImageContent | WorksVideoContent

export interface WorksCredit {
  kindof: string
  name: string
}

export interface WorksItem {
  id: number
  title: string
  slug: string
  date: string
  modified: string
  link: string
  measures: WorksCategory[]
  featuredImage?: string
  description?: string
  contents?: WorksContent[]
  credits?: WorksCredit[]
  thumbnailUrl: string
  client: string
  business: WorksCategory[]
}

export function getWorksImageSrc(image?: WorksImage): string {
  if (!image) return ''
  if (image.sizes && image.sizes.large) return image.sizes.large
  return image.url || ''
}

export function getWorksVideoSrc(video?: WorksVideo): string {
  if (!video) return ''
  return video.url || video.link || ''
}

export function findFirstWorksImage(contents?: WorksContent[]): WorksImage | undefined {
  if (!contents) return undefined
  const imageContent = contents.find((content): content is WorksImageContent => {
    return content.acf_fc_layout === 'image' && !!(content as WorksImageContent).source
  })
  return imageContent ? imageContent.source : undefined
}

export function findFirstWorksVideo(contents?: WorksContent[]): WorksVideo | undefined {
  if (!contents) return undefined
  const videoContent = contents.find((content): content is WorksVideoContent => {
    return content.acf_fc_layout === 'video' && !!(content as WorksVideoContent).source
  })
  return videoContent ? videoContent.source : undefined
}

export function formatWorksItem(post: any): WorksItem {
  const embedded = post._embedded || {}
  const terms = embedded['wp:term'] || []
  const measures: any[] = []
  const client: any[] = []
  const business: any[] = []

  terms.forEach((group: any[]) => {
    if (!group || group.length === 0) return
    const taxonomy = group[0].taxonomy
    if (taxonomy === 'measures') {
      measures.push(...group)
    } else if (taxonomy === 'client') {
      client.push(...group)
    } else if (taxonomy === 'business') {
      business.push(...group)
    }
  })

  const featuredImage = embedded['wp:featuredmedia'] && embedded['wp:featuredmedia'][0]
    ? embedded['wp:featuredmedia'][0].source_url
    : ''

  const contents: WorksContent[] = (post.acf && post.acf.contents) || []
  const firstContentImage = findFirstWorksImage(contents)
  const thumbnailUrl = featuredImage || getWorksImageSrc(firstContentImage) || ''

  return {
    id: post.id,
    title: post.title && post.title.rendered ? post.title.rendered : '',
    slug: post.slug,
    date: post.date,
    modified: post.modified,
    link: post.link,
    measures: measures.map((c: any) => ({ id: c.id, name: c.name, slug: c.slug })),
    featuredImage,
    description: post.acf && post.acf.description ? post.acf.description : '',
    contents,
    credits: (post.acf && post.acf.credits) || [],
    thumbnailUrl,
    client: client.map((t: any) => t.name).join(' / '),
    business: business.map((c: any) => ({ id: c.id, name: c.name, slug: c.slug }))
  }
}
